import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, History, Loader2 } from "lucide-react";

import { api, type Camera } from "../api";
import { CameraTimeline, todayValue } from "../components/CameraTimeline";
import { PageHeader } from "../components/PageHeader";
import { SearchableSelect } from "../components/SearchableSelect";
import { useScope } from "../components/ScopeContext";
import { useWords } from "../words";

const CAMERA_STORAGE_KEY = "bf-recordings-camera";

function shiftDate(value: string, days: number): string {
  const [y, m, d] = value.split("-").map(Number);
  const next = new Date(y, (m || 1) - 1, (d || 1) + days);
  return [
    next.getFullYear(),
    String(next.getMonth() + 1).padStart(2, "0"),
    String(next.getDate()).padStart(2, "0"),
  ].join("-");
}

function readInitialCamera(): string {
  const fromUrl = new URLSearchParams(window.location.search).get("camera");
  if (fromUrl) return fromUrl;
  try {
    return localStorage.getItem(CAMERA_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function persistCamera(id: string) {
  const p = new URLSearchParams(window.location.search);
  if (id) p.set("camera", id);
  else p.delete("camera");
  const qs = p.toString();
  const next = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  window.history.replaceState(null, "", next);
  try {
    if (id) localStorage.setItem(CAMERA_STORAGE_KEY, id);
    else localStorage.removeItem(CAMERA_STORAGE_KEY);
  } catch {
    /* private browsing / storage disabled */
  }
}

export default function Recordings() {
  const { scopeParams, scopeKey, scopeLabel, scope } = useScope();
  const w = useWords();
  const [cameraId, setCameraId] = useState(readInitialCamera);
  const [date, setDate] = useState(todayValue);
  const today = todayValue();

  const camerasQuery = useQuery<Camera[]>({
    queryKey: ["cameras", scopeKey],
    queryFn: () => api.cameras.list(scopeParams),
    staleTime: 60_000,
  });

  const cameras = useMemo(() => {
    const list = camerasQuery.data ?? [];
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [camerasQuery.data]);

  useEffect(() => {
    if (cameras.length === 0) return;
    if (cameraId && cameras.some((c) => c.id === cameraId)) {
      persistCamera(cameraId);
      return;
    }
    const next = cameras[0].id;
    setCameraId(next);
    persistCamera(next);
  }, [cameras, cameraId]);

  const selected = cameras.find((c) => c.id === cameraId);

  function pickCamera(id: string) {
    setCameraId(id);
    persistCamera(id);
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <PageHeader
        className="shrink-0"
        title="Playback"
        description="Rewatch recorded video from a camera. Check for activities and alerts on the timeline."
        icon={History}
      />

      <div className="flex shrink-0 flex-wrap items-center gap-3 rounded-xl border border-white/5 bg-bf-surface/30 p-3 shadow-sm">
        <label htmlFor="recordings-camera" className="text-sm font-medium text-bf-muted">
          Camera
        </label>
        <SearchableSelect
          id="recordings-camera"
          className="w-72"
          ariaLabel="Select camera"
          value={cameraId}
          onChange={pickCamera}
          placeholder="Select a camera"
          options={cameras.map((cam) => ({
            value: cam.id,
            label: cam.name,
            hint: cam.store || undefined,
            keywords: cam.id,
            badge: !cam.enabled ? "Off" : cam.role === "view_only" ? "Non-AI" : undefined,
          }))}
        />

        <div className="h-6 w-px bg-white/10" />

        <label htmlFor="recordings-date" className="text-sm font-medium text-bf-muted">
          Date
        </label>
        <input
          id="recordings-date"
          type="date"
          aria-label="Recording date"
          className="rounded-lg border border-white/10 bg-bf-surface/60 px-3 py-2 text-sm font-medium text-bf-text"
          value={date}
          max={today}
          onChange={(e) => {
            const next = e.target.value;
            setDate(next && next > today ? today : next);
          }}
        />
        <div className="flex flex-col overflow-hidden rounded-lg border border-white/10 bg-bf-surface/60">
          <button
            type="button"
            className="px-2 py-0.5 text-bf-text transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Next day"
            disabled={date >= today}
            onClick={() => {
              const next = shiftDate(date, 1);
              setDate(next > today ? today : next);
            }}
          >
            <ChevronUp size={16} />
          </button>
          <button
            type="button"
            className="border-t border-white/10 px-2 py-0.5 text-bf-text transition hover:bg-white/10"
            aria-label="Previous day"
            onClick={() => setDate(shiftDate(date, -1))}
          >
            <ChevronDown size={16} />
          </button>
        </div>
      </div>

      {camerasQuery.isLoading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center rounded-xl border border-white/5 bg-bf-surface/30">
          <Loader2 size={32} className="animate-spin text-bf-muted" />
        </div>
      ) : cameras.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-white/5 bg-bf-surface/30 text-center text-bf-muted">
          <History size={32} />
          <p className="text-sm">
            {scope.kind === "all"
              ? "No cameras. Add one in Settings."
              : `No cameras in ${scopeLabel}.`}
          </p>
          {scope.kind !== "all" && (
            <p className="max-w-sm text-xs">
              Switch to another {w.store} or {w.group} using the picker at the
              top of the page, or add cameras to this one in Settings.
            </p>
          )}
        </div>
      ) : selected ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-white/5 bg-bf-surface/40 p-3 shadow-2xl">
          <CameraTimeline
            key={selected.id}
            cameraId={selected.id}
            cameraName={selected.name}
            scope={scopeParams}
            date={date}
          />
        </div>
      ) : null}
    </div>
  );
}
